import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTaskById, useTasks, useTaskComments } from '@/hooks/useTasks';
import { useAuth } from '@/hooks/useAuth';
import { useLeadDepartment, useDepartmentById } from '@/hooks/useConfig';
import { useWorkflows } from '@/hooks/useWorkflows';
import { useUsers } from '@/hooks/useUsers';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArrowLeft, Trash2, Calendar, User, MessageSquare, Paperclip, X, CalendarCheck, ListTodo, Flag, Pencil } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { format } from 'date-fns';
import { useState, useRef } from 'react';
import { getErrorMessage } from '@/lib/utils';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api';
import { TaskType, TaskPriority, TaskUpdateRequest } from '@/lib/types';

const TASK_TYPES: TaskType[] = ['research', 'competitive_analysis', 'client_follow_up', 'reporting', 'other'];
const TASK_PRIORITIES: TaskPriority[] = ['low', 'medium', 'high'];

function taskTypeLabel(t: TaskType): string {
  return t.replace(/_/g, ' ');
}

function toDateInputValue(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

type TaskEditBaseline = {
  title: string;
  description: string;
  type: TaskType;
  priority: TaskPriority;
  assigneesIds: string[];
  startDate: string;
  dueDate: string;
  attachmentIds: string[];
};

function assigneesEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((id, i) => id === sb[i]);
}

export function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isTeamLeader } = useAuth();
  const { leadDepartmentId } = useLeadDepartment();
  const { data: leadDepartment } = useDepartmentById(leadDepartmentId ?? undefined);
  const { users } = useUsers();
  const { workflows } = useWorkflows();
  const { data: task, isLoading, error } = useTaskById(id);
  const { updateTask, updateTaskStatus, deleteTask } = useTasks();
  const { comments, addComment, deleteComment } = useTaskComments(id);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [newCommentContent, setNewCommentContent] = useState('');
  const [pendingAttachmentIds, setPendingAttachmentIds] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<Array<{ file: File; attachmentId?: string; name: string }>>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  const [editUploading, setEditUploading] = useState(false);
  const [editUploadedNames, setEditUploadedNames] = useState<Record<string, string>>({});
  const [editOpen, setEditOpen] = useState(false);
  const [editBaseline, setEditBaseline] = useState<TaskEditBaseline | null>(null);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    type: 'other' as TaskType,
    priority: 'medium' as TaskPriority,
    assigneesIds: [] as string[],
    startDate: '',
    dueDate: '',
    attachmentIds: [] as string[],
  });

  const departmentMemberIds = new Set<string>();
  if (leadDepartment) {
    (leadDepartment.agents ?? []).forEach((a) => departmentMemberIds.add((a as { id: string }).id));
    const leaders = leadDepartment.teamLeaders ?? (leadDepartment.teamLeader ? [leadDepartment.teamLeader] : []);
    leaders.forEach((l) => {
      const uid = (l as { user?: { id: string }; id?: string }).user?.id ?? (l as { id: string }).id;
      if (uid) departmentMemberIds.add(uid);
    });
  }
  const assigneeOptions = users.filter(
    (u) => departmentMemberIds.has(u.id) || (u.user_id && departmentMemberIds.has(u.user_id))
  );
  const fromKanban = searchParams.get('from') === 'kanban';
  const backTo = fromKanban ? '/admin/kanban?view=list' : '/admin/tasks';

  const departmentId = isTeamLeader ? leadDepartmentId : task?.departmentId;
  const departmentWorkflow = departmentId && workflows.length > 0
    ? workflows.find((w) => (w as { departments?: Array<{ department: { id: string } }> }).departments?.some((d) => d.department?.id === departmentId))
    : null;
  const workflowStages = departmentWorkflow?.workflowStages
    ? [...departmentWorkflow.workflowStages].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)).map((ws) => ws.stage)
    : [];

  const handleStageChange = (stageId: string) => {
    if (!id || task?.stageId === stageId) return;
    updateTaskStatus.mutate(
      { id, stageId },
      {
        onError: (e) => toast.error(getErrorMessage(e, 'Failed to update stage')),
      }
    );
  };

  const handleDelete = () => {
    if (!id) return;
    deleteTask.mutate(id, {
      onSuccess: () => {
        setShowDeleteConfirm(false);
        navigate(backTo);
      },
      onError: (e) => toast.error(getErrorMessage(e, 'Failed to delete task')),
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    e.target.value = '';
    setUploading(true);
    const ids: string[] = [];
    const pending: Array<{ file: File; attachmentId?: string; name: string }> = [];
    for (const file of files) {
      try {
        const prepare = await apiClient.prepareUpload({
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          uploadedByRequester: false,
        });
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', prepare.uploadUrl, true);
        Object.entries(prepare.headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
        await new Promise<void>((resolve, reject) => {
          xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('Upload failed')));
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.send(file);
        });
        const verified = await apiClient.verifyUpload(prepare.attachmentId);
        if (verified) {
          ids.push(prepare.attachmentId);
          pending.push({ file, attachmentId: prepare.attachmentId, name: file.name });
        }
      } catch (err) {
        console.error(err);
      }
    }
    setUploading(false);
    setPendingAttachmentIds((prev) => [...prev, ...ids]);
    setPendingFiles((prev) => [...prev, ...pending]);
  };

  const removePendingAttachment = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
    setPendingAttachmentIds((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEditFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    e.target.value = '';
    setEditUploading(true);
    const newIds: string[] = [];
    const nameById: Record<string, string> = {};
    for (const file of files) {
      try {
        const prepare = await apiClient.prepareUpload({
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          uploadedByRequester: false,
        });
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', prepare.uploadUrl, true);
        Object.entries(prepare.headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
        await new Promise<void>((resolve, reject) => {
          xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('Upload failed')));
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.send(file);
        });
        const verified = await apiClient.verifyUpload(prepare.attachmentId);
        if (verified) {
          newIds.push(prepare.attachmentId);
          nameById[prepare.attachmentId] = file.name;
        }
      } catch (err) {
        console.error(err);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setEditUploading(false);
    if (newIds.length > 0) {
      setEditForm((p) => ({ ...p, attachmentIds: [...p.attachmentIds, ...newIds] }));
      setEditUploadedNames((prev) => ({ ...prev, ...nameById }));
    }
  };

  const removeEditAttachment = (attachmentId: string) => {
    setEditForm((p) => ({
      ...p,
      attachmentIds: p.attachmentIds.filter((x) => x !== attachmentId),
    }));
    setEditUploadedNames((prev) => {
      const next = { ...prev };
      delete next[attachmentId];
      return next;
    });
  };

  const getEditAttachmentLabel = (attId: string) =>
    task?.attachments?.find((a) => a.id === attId)?.fileName ?? editUploadedNames[attId] ?? 'Attachment';

  const openEditDialog = () => {
    if (!task) return;
    const assigneeUserIds = (task.assignees ?? [])
      .map((a) => (a as { user?: { id: string } }).user?.id)
      .filter((x): x is string => Boolean(x));
    const startDate = toDateInputValue(task.startDate);
    const dueDate = toDateInputValue(task.dueDate);
    const attachmentIds = (task.attachments ?? []).map((a) => a.id);
    setEditUploadedNames({});
    setEditBaseline({
      title: task.title,
      description: task.description,
      type: task.type,
      priority: task.priority,
      assigneesIds: [...assigneeUserIds],
      startDate,
      dueDate,
      attachmentIds: [...attachmentIds],
    });
    setEditForm({
      title: task.title,
      description: task.description,
      type: task.type,
      priority: task.priority,
      assigneesIds: assigneeUserIds,
      startDate,
      dueDate,
      attachmentIds: [...attachmentIds],
    });
    setEditOpen(true);
  };

  const toggleEditAssignee = (userId: string) => {
    setEditForm((prev) => ({
      ...prev,
      assigneesIds: prev.assigneesIds.includes(userId)
        ? prev.assigneesIds.filter((x) => x !== userId)
        : [...prev.assigneesIds, userId],
    }));
  };

  const handleSaveEdit = () => {
    if (!id || !task || !editBaseline) return;
    const title = editForm.title.trim();
    const description = editForm.description.trim();
    const data: TaskUpdateRequest = {};

    if (title !== editBaseline.title.trim()) {
      if (!title) {
        toast.error('Title is required');
        return;
      }
      data.title = title;
    }
    if (description !== editBaseline.description.trim()) {
      if (description.length < 10) {
        toast.error('Description must be at least 10 characters');
        return;
      }
      data.description = description;
    }
    if (editForm.type !== editBaseline.type) {
      data.type = editForm.type;
    }
    if (editForm.priority !== editBaseline.priority) {
      data.priority = editForm.priority;
    }
    if (!assigneesEqual(editForm.assigneesIds, editBaseline.assigneesIds)) {
      data.assigneesIds = editForm.assigneesIds;
    }
    if (editForm.startDate !== editBaseline.startDate) {
      if (editForm.startDate) {
        data.startDate = new Date(`${editForm.startDate}T00:00:00`).toISOString();
      }
    }
    if (editForm.dueDate !== editBaseline.dueDate) {
      if (editForm.dueDate) {
        data.dueDate = new Date(`${editForm.dueDate}T23:59:59.999`).toISOString();
      }
    }
    if (!assigneesEqual(editForm.attachmentIds, editBaseline.attachmentIds)) {
      data.attachments = editForm.attachmentIds;
    }

    if (Object.keys(data).length === 0) {
      toast.info('No changes to save');
      return;
    }

    const effectiveStart = data.startDate
      ? new Date(data.startDate)
      : task.startDate
        ? new Date(task.startDate)
        : null;
    const effectiveDue = data.dueDate
      ? new Date(data.dueDate)
      : task.dueDate
        ? new Date(task.dueDate)
        : null;
    if (
      effectiveStart &&
      effectiveDue &&
      !Number.isNaN(effectiveStart.getTime()) &&
      !Number.isNaN(effectiveDue.getTime()) &&
      effectiveDue <= effectiveStart
    ) {
      toast.error('Due date must be after start date');
      return;
    }

    updateTask.mutate(
      { id, data },
      {
        onSuccess: () => {
          setEditOpen(false);
          setEditUploadedNames({});
        },
      }
    );
  };

  const handleAddComment = () => {
    const content = newCommentContent.trim();
    if (!content && pendingAttachmentIds.length === 0) return;
    addComment.mutate(
      {
        content: content || '(Attachment)',
        attachmentsIds: pendingAttachmentIds.length > 0 ? pendingAttachmentIds : undefined,
      },
      {
        onSuccess: () => {
          setNewCommentContent('');
          setPendingAttachmentIds([]);
          setPendingFiles([]);
        },
      },
    );
  };

  if (!id) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" asChild><Link to={backTo}><ArrowLeft className="h-4 w-4 mr-2" />Back to tasks</Link></Button>
        <p className="text-muted-foreground">Invalid task ID.</p>
      </div>
    );
  }

  if (isLoading || !task) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" asChild><Link to={backTo}><ArrowLeft className="h-4 w-4 mr-2" />Back to tasks</Link></Button>
        <div className="flex justify-center py-12 text-muted-foreground">{error ? 'Failed to load task' : 'Loading task...'}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to={backTo}><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{task.title}</h1>
            <p className="text-muted-foreground text-sm">
              Created {format(new Date(task.createdAt), 'MMM d, yyyy')}
              {task.createdBy?.profile?.name && ` by ${task.createdBy.profile.name}`}
            </p>
          </div>
        </div>
        {(workflowStages.length > 0 || isTeamLeader) && (
          <div className="flex items-center gap-2">
            {workflowStages.length > 0 && (
              <Select value={task.stageId} onValueChange={handleStageChange} disabled={updateTaskStatus.isPending}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Stage" />
                </SelectTrigger>
                <SelectContent>
                  {workflowStages.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      <span className="flex items-center gap-2">
                        {s.color && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />}
                        {s.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {isTeamLeader && (
              <>
                <Button variant="outline" size="sm" onClick={openEditDialog} disabled={updateTask.isPending}>
                  <Pencil className="h-4 w-4 mr-1" /> Edit
                </Button>
                <Button variant="destructive" size="sm" onClick={() => setShowDeleteConfirm(true)} disabled={deleteTask.isPending}>
                  <Trash2 className="h-4 w-4 mr-1" /> Delete
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ListTodo className="h-4 w-4" />
              Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1.5">Description</p>
              <p className="text-sm whitespace-pre-wrap break-words max-h-48 overflow-y-auto rounded-md bg-muted/50 p-3 border border-border/50">
                {task.description}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <div className="flex items-center gap-2">
                <ListTodo className="h-4 w-4 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Type</p>
                  <p className="font-medium capitalize mt-0.5">{task.type.replace(/_/g, ' ')}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Flag className="h-4 w-4 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Priority</p>
                  <p className="font-medium capitalize mt-0.5">{task.priority}</p>
                </div>
              </div>
              <div className="col-span-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Stage</p>
                <span
                  className="inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium border"
                  style={{ borderColor: task.stage?.color ?? '#ccc', backgroundColor: `${task.stage?.color ?? '#eee'}20` }}
                >
                  {task.stage?.name ?? '—'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Start date</p>
                  <p className="font-medium mt-0.5">
                    {task.startDate ? format(new Date(task.startDate), 'MMM d, yyyy') : '—'}
                  </p>
                </div>
              </div>
              {task.dueDate ? (
                <div className="flex items-center gap-2">
                  <CalendarCheck className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Due date</p>
                    <p className="font-medium mt-0.5">{format(new Date(task.dueDate), 'MMM d, yyyy')}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <CalendarCheck className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Due date</p>
                    <p className="font-medium mt-0.5 text-muted-foreground">—</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <User className="h-4 w-4" />
              Assignees
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(task.assignees ?? []).length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center rounded-lg border border-dashed border-border bg-muted/30">
                <User className="h-10 w-10 text-muted-foreground/50 mb-2" />
                <p className="text-sm font-medium text-muted-foreground">No assignees</p>
                <p className="text-xs text-muted-foreground mt-0.5">This task is not assigned to anyone yet.</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {(task.assignees ?? []).map((a, idx) => {
                  const user = (a as { user?: { id: string; email?: string; profile?: { name?: string } } }).user;
                  const name = user?.profile?.name ?? user?.email ?? '—';
                  const initial = name !== '—' ? name.charAt(0).toUpperCase() : '?';
                  return (
                    <li
                      key={user?.id ?? `assignee-${idx}`}
                      className="flex items-center gap-3 rounded-lg border border-border/60 bg-muted/20 p-3 transition-colors hover:bg-muted/40"
                    >
                      <Avatar className="h-9 w-9 shrink-0 border-2 border-background">
                        <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                          {initial}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{name}</p>
                        {user?.email && name !== user.email && (
                          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {(task.attachments ?? []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Attachments</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {task.attachments?.map((att) => (
                <li key={att.id}>
                  <a
                    href={(att as { downloadUrl?: string }).downloadUrl ?? att.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline text-sm"
                  >
                    {att.fileName}
                  </a>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Internal chat
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ul className="space-y-4 max-h-[320px] overflow-y-auto">
            {comments.length === 0 ? (
              <li className="text-sm text-muted-foreground py-4">No comments yet. Add a message below.</li>
            ) : (
              comments.map((c) => (
                <li key={c.id} className="border-b border-border pb-3 last:border-0 last:pb-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground">
                        {c.user?.profile?.name ?? c.user?.email ?? 'Unknown'}
                      </p>
                      <p className="text-sm whitespace-pre-wrap mt-0.5">{c.content}</p>
                      {(c.attachments?.length ?? 0) > 0 && (
                        <ul className="mt-2 space-y-1">
                          {c.attachments?.map((att) => (
                            <li key={att.id}>
                              <a
                                href={att.downloadUrl ?? att.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                              >
                                <Paperclip className="h-3 w-3" />
                                {att.fileName}
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {format(new Date(c.createdAt), 'MMM d, yyyy HH:mm')}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => deleteComment.mutate(c.id)}
                      disabled={deleteComment.isPending}
                      title="Delete comment"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              ))
            )}
          </ul>
          <div className="space-y-2 pt-2 border-t border-border">
            <Textarea
              placeholder="Add a message..."
              value={newCommentContent}
              onChange={(e) => setNewCommentContent(e.target.value)}
              rows={3}
              className="resize-none"
              disabled={addComment.isPending}
            />
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileSelect}
              disabled={uploading}
            />
            {pendingFiles.length > 0 && (
              <ul className="flex flex-wrap gap-2 text-sm">
                {pendingFiles.map((p, i) => (
                  <li
                    key={i}
                    className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1"
                  >
                    <Paperclip className="h-3 w-3 text-muted-foreground" />
                    <span className="truncate max-w-[120px]">{p.name}</span>
                    <button
                      type="button"
                      onClick={() => removePendingAttachment(i)}
                      className="text-muted-foreground hover:text-foreground"
                      aria-label="Remove"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || addComment.isPending}
              >
                <Paperclip className="h-4 w-4 mr-1" />
                {uploading ? 'Uploading...' : 'Attach files'}
              </Button>
              <Button
                onClick={handleAddComment}
                disabled={
                  addComment.isPending ||
                  uploading ||
                  ((!newCommentContent.trim()) && pendingAttachmentIds.length === 0)
                }
              >
                {addComment.isPending ? 'Sending...' : 'Send'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={editOpen}
        onOpenChange={(open) => {
          setEditOpen(open);
          if (!open) {
            setEditBaseline(null);
            setEditUploadedNames({});
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit task</DialogTitle>
            <DialogDescription>
              Update task details, assignees, and attachments. Replacing attachments.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={editForm.title}
                onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))}
                maxLength={100}
              />
            </div>
            <div className="space-y-2">
              <Label>Description (min 10 characters)</Label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
                rows={4}
                className="resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={editForm.type} onValueChange={(v) => setEditForm((p) => ({ ...p, type: v as TaskType }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TASK_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {taskTypeLabel(t)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={editForm.priority} onValueChange={(v) => setEditForm((p) => ({ ...p, priority: v as TaskPriority }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TASK_PRIORITIES.map((p) => (
                      <SelectItem key={p} value={p}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start date</Label>
                <Input
                  type="date"
                  value={editForm.startDate}
                  onChange={(e) => setEditForm((p) => ({ ...p, startDate: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Due date</Label>
                <Input
                  type="date"
                  value={editForm.dueDate}
                  onChange={(e) => setEditForm((p) => ({ ...p, dueDate: e.target.value }))}
                />
              </div>
            </div>
            {assigneeOptions.length > 0 && (
              <div className="space-y-2">
                <Label>Assignees</Label>
                <div className="flex flex-wrap gap-2 border rounded-md p-2 max-h-40 overflow-y-auto">
                  {assigneeOptions.map((u) => {
                    const userId = u.user_id ?? u.id;
                    const checked = editForm.assigneesIds.includes(userId);
                    return (
                      <label key={userId} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleEditAssignee(userId)}
                          className="rounded border-input"
                        />
                        {u.name}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            {isTeamLeader && (
              <div className="space-y-2">
                <Label className='me-2'>Attachments</Label>
                <input
                  ref={editFileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleEditFileSelect}
                  disabled={editUploading}
                />
                {editForm.attachmentIds.length > 0 && (
                  <ul className="flex flex-col gap-2 text-sm max-h-36 overflow-y-auto">
                    {editForm.attachmentIds.map((attId) => (
                      <li
                        key={attId}
                        className="inline-flex items-center justify-between gap-2 rounded-md bg-muted px-2 py-1.5"
                      >
                        <span className="flex items-center gap-1 min-w-0 truncate">
                          <Paperclip className="h-3 w-3 text-muted-foreground shrink-0" />
                          <span className="truncate">{getEditAttachmentLabel(attId)}</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => removeEditAttachment(attId)}
                          className="text-muted-foreground hover:text-foreground shrink-0"
                          aria-label="Remove attachment"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => editFileInputRef.current?.click()}
                  disabled={editUploading || updateTask.isPending}
                >
                  <Paperclip className="h-4 w-4 mr-1" />
                  {editUploading ? 'Uploading...' : 'Add attachments'}
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={updateTask.isPending || editUploading}>
              {updateTask.isPending ? 'Saving...' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete task?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &quot;{task.title}&quot;. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleteTask.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
